/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

/**
 * Known mapping from singular route prefixes to their plural list routes.
 */
const SINGULAR_TO_LIST_ROUTE = {
  "travel-order": "/travel-orders",
  "travel-order-pax": "/travel-order-pax",
  "travel-invoice": "/travel-invoices",
  "travel-invoice-line-item": "/travel-invoice-line-items",
  "guide-trip-note": "/guide-trip-notes",
  "referral-code": "/referral-codes",
  creator: "/creators",
  "creator-onboarding": "/creator-onboardings",
  "guide-allocation": "/guide-allocations",
  customer: "/customers",
  opportunity: "/opportunities",
  coupon: "/coupons",
  "pricing-category": "/pricing-categories",
  "customer-coupon": "/customer-coupons",
  "loyalty-rule": "/loyalty-rules",
  "loyalty-ledger": "/loyalty-ledgers",
  "loyalty-summary": "/loyalty-summaries",
  "payment-transaction": "/payment-transactions",
  "payment-term": "/payment-terms",
  "training-completion": "/training-completions",
  "travel-contact": "/travel-contacts",
  "order-cancellation": "/order-cancellations",
  "order-comm": "/order-comms",
  "order-coupon": "/order-coupons",
  "order-itinerary": "/order-itineraries",
  merchant: "/merchants",
  "merchant-allocation": "/merchant-allocations",
  "merchant-order-line": "/merchant-order-lines",
  "merchant-asset-inventory": "/merchant-asset-inventories",
  "travel-order-item": "/travel-order-items",
};

/**
 * Display names for segments where humanise doesn't match the desired branding.
 */
const SEGMENT_DISPLAY_NAMES = {
  trips: "Travel Destinations",
  "travel-order": "Travel Orders",
  "travel-orders": "Travel Orders",
  "trip-packages": "Trip Packages",
  coupon: "Coupons",
  coupons: "Coupons",
  "order-cancellation": "Order Cancellations",
  "order-cancellations": "Order Cancellations",
};

function humaniseSegment(segment) {
  if (SEGMENT_DISPLAY_NAMES[segment]) return SEGMENT_DISPLAY_NAMES[segment];
  const decoded = decodeURIComponent(segment).replace(/[-_]/g, " ");
  return decoded.charAt(0).toUpperCase() + decoded.slice(1);
}

/**
 * Builds the list of breadcrumb items [{ label, to }] for a route array.
 * Omits structural action keywords like "detail" that do not represent separate pages,
 * handles package-edit multi-tier crumbs, and maps singular prefixes to their plural list routes.
 */
export function buildBreadcrumbs(segments) {
  if (!segments || segments.length === 0) return [];

  // Special case: package edit is /trips/:tripId/packages/:packageId/edit
  const isPackageEdit =
    segments[0] === "trips" &&
    segments[2] === "packages" &&
    segments[segments.length - 1] === "edit" &&
    segments.length >= 5;

  if (isPackageEdit) {
    const tripId = segments[1];
    const packageId = segments[3];
    return [
      { label: "Travel Destinations", to: "/trips" },
      { label: `#${tripId}`, to: `/trips/detail/${tripId}` },
      { label: "Trip Packages", to: "/trip-packages" },
      { label: `#${packageId}`, to: `/trip-packages/detail/${packageId}` },
      { label: "Edit", to: null },
    ];
  }

  // Filter out internal non-page keyword segments like "detail"
  const filtered = [];
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    if (seg === "detail" && i > 0 && i < segments.length - 1) {
      // Skip the "detail" keyword when it sits between module and id (e.g. /trips/detail/71)
      continue;
    }
    filtered.push(seg);
  }

  if (filtered.length === 0) return [];

  const crumbs = [];
  for (let i = 0; i < filtered.length; i += 1) {
    const seg = filtered[i];
    const isLast = i === filtered.length - 1;

    let label = humaniseSegment(seg);
    let to = null;

    if (!isLast) {
      if (i === 0) {
        to = SINGULAR_TO_LIST_ROUTE[seg] || `/${seg}`;
      } else {
        to = `/${filtered.slice(0, i + 1).join("/")}`;
      }
    }

    crumbs.push({ label, to });
  }

  return crumbs;
}

function Breadcrumbs({ icon, title, route, light }) {
  const segments = Array.isArray(route) ? route : (route ? String(route).split("/").filter(Boolean) : []);
  const crumbs = buildBreadcrumbs(segments);

  if (crumbs.length === 0) return null;

  const parentCrumbs = crumbs.slice(0, -1);
  const currentCrumb = crumbs[crumbs.length - 1];

  return (
    <MDBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600]),
          },
        }}
      >
        <Link to="/dashboard">
          <MDTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </MDTypography>
        </Link>
        {parentCrumbs.map((crumb, index) =>
          crumb.to ? (
            <Link to={crumb.to} key={`${crumb.label}-${index}`}>
              <MDTypography
                component="span"
                variant="button"
                fontWeight="regular"
                textTransform="capitalize"
                color={light ? "white" : "dark"}
                opacity={light ? 0.8 : 0.5}
                sx={{ lineHeight: 0 }}
              >
                {crumb.label}
              </MDTypography>
            </Link>
          ) : (
            <MDTypography
              key={`${crumb.label}-${index}`}
              component="span"
              variant="button"
              fontWeight="regular"
              textTransform="capitalize"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              {crumb.label}
            </MDTypography>
          )
        )}
        <MDTypography
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          color={light ? "white" : "dark"}
          sx={{ lineHeight: 0 }}
        >
          {currentCrumb.label || (title ? title.replace("-", " ") : "")}
        </MDTypography>
      </MuiBreadcrumbs>
    </MDBox>
  );
}

Breadcrumbs.defaultProps = {
  light: false,
};

Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
